import React, { useMemo, useState } from "react";
import { Star, ShoppingBag, Heart, Eye } from "lucide-react";
import { products, productCategories } from "../mock";
import { SectionTitle } from "./Sections";

const ProductCard = ({ p, onAdd, onQuickView, onWish, wished }) => (
  <div className="group flex flex-col">
    <div className="relative zoom-wrap aspect-[4/5] bg-[color:var(--brand-cream)] rounded-sm">
      <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
      {p.badge && (
        <span className="absolute top-3 left-3 bg-[color:var(--brand-red)] text-white text-[10px] uppercase tracking-widest px-2.5 py-1">
          {p.badge}
        </span>
      )}
      <button
        onClick={() => onWish(p)}
        aria-label="Add to wishlist"
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-sm transition-colors"
      >
        <Heart className={`w-4 h-4 ${wished ? "fill-[color:var(--brand-red)] text-[color:var(--brand-red)]" : "text-neutral-700"}`} />
      </button>
      <button
        onClick={() => onQuickView(p)}
        className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all bg-white text-neutral-800 text-xs font-medium px-3 py-2 flex items-center gap-1.5 shadow"
      >
        <Eye className="w-3.5 h-3.5" /> Quick View
      </button>
    </div>
    <div className="pt-3 flex-1 flex flex-col">
      <div className="text-[11px] uppercase tracking-widest text-neutral-500">{p.category}</div>
      <div className="mt-1 text-sm md:text-[15px] font-medium leading-snug group-hover:text-[color:var(--brand-red)] transition-colors">
        {p.name}
      </div>
      <div className="mt-1.5 flex items-center gap-1 text-xs text-neutral-600">
        <Star className="w-3.5 h-3.5 fill-[color:var(--brand-red)] text-[color:var(--brand-red)]" />
        <span className="font-medium text-neutral-800">{p.rating}</span>
        <span>({p.reviews})</span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-base font-semibold text-[color:var(--brand-ink)]">${p.price.toFixed(2)}</span>
        {p.oldPrice && (
          <span className="text-xs text-neutral-500 line-through">${p.oldPrice.toFixed(2)}</span>
        )}
      </div>
      <button
        onClick={() => onAdd(p)}
        className="mt-3 w-full bg-[color:var(--brand-ink)] text-white py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-[color:var(--brand-red)] transition-colors flex items-center justify-center gap-2"
      >
        <ShoppingBag className="w-4 h-4" /> Add to Bag
      </button>
    </div>
  </div>
);

const ProductGrid = React.forwardRef(({ activeCategory, setActiveCategory, onAdd, onQuickView, onWish, wishlist }, ref) => {
  const [visible, setVisible] = useState(8);

  const filtered = useMemo(() => {
    if (!activeCategory || activeCategory === "All") return products;
    return products.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  const shown = filtered.slice(0, visible);

  return (
    <section ref={ref} id="products" className="px-4 md:px-10 py-14 bg-white scroll-mt-32">
      <SectionTitle subtitle="Discover our latest, most-loved finds — always crafted, always in style.">
        Shop Our Collection
      </SectionTitle>

      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-8">
        {productCategories.map((c) => (
          <button
            key={c}
            onClick={() => { setActiveCategory(c); setVisible(8); }}
            className={`px-4 py-2 text-xs font-medium uppercase tracking-widest border transition-colors ${
              (activeCategory || "All") === c
                ? "bg-[color:var(--brand-ink)] text-white border-[color:var(--brand-ink)]"
                : "bg-white text-neutral-700 border-neutral-300 hover:border-[color:var(--brand-ink)]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {shown.map((p) => (
          <ProductCard
            key={p.id}
            p={p}
            onAdd={onAdd}
            onQuickView={onQuickView}
            onWish={onWish}
            wished={wishlist.includes(p.id)}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-14 text-neutral-500">
          No products in this category yet. Check back soon.
        </div>
      )}

      {visible < filtered.length && (
        <div className="text-center mt-10">
          <button
            onClick={() => setVisible((v) => v + 8)}
            className="border border-[color:var(--brand-ink)] text-[color:var(--brand-ink)] px-8 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[color:var(--brand-ink)] hover:text-white transition-colors"
          >
            Load More
          </button>
        </div>
      )}
    </section>
  );
});

ProductGrid.displayName = "ProductGrid";
export default ProductGrid;
